import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { getToken, setToken as persistToken, clearToken } from "../services/http";

export type AuthUser = {
  id: number;
  email: string;
  full_name: string;
  role: string;
  org_id?: number | null;
};

type AuthState = {
  token: string | null;
  user: AuthUser | null;
  login: (token: string, user: AuthUser) => void;
  // keepDraft: token süresi dolduğunda (istemsiz) true geçilir — kullanıcının
  // GSPR taslağı ve kimliği korunur ki geri dönüp login olunca kaldığı yerden
  // devam etsin. Bilinçli çıkışta (buton) parametresiz çağrılır → tam temizlik.
  logout: (opts?: { keepDraft?: boolean }) => void;
};

const USER_KEY = "medreg_user";
const AuthCtx = createContext<AuthState | null>(null);

// Müşteriye özel istemci taslakları (sunucu verisi değil, tarayıcı cache'i).
// Oturum değişiminde temizlenir — aksi halde aynı tarayıcıda başka müşteriye
// önceki müşterinin GSPR taslağı sızar.
const USER_SCOPED_KEYS = ["gspr_session"];
function clearUserScopedState() {
  for (const k of USER_SCOPED_KEYS) localStorage.removeItem(k);
}

function readUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTok] = useState<string | null>(() => getToken());
  const [user, setUser] = useState<AuthUser | null>(() => readUser());

  const login = (t: string, u: AuthUser) => {
    // Farklı müşteri girişinde önceki taslakları taşıma.
    if (u.id !== readUser()?.id) clearUserScopedState();
    persistToken(t);
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    setTok(t);
    setUser(u);
  };

  const logout = (opts?: { keepDraft?: boolean }) => {
    clearToken();
    setTok(null);
    setUser(null);
    // Token süresi dolduğunda (keepDraft) kimlik + taslak KORUNUR: aynı kullanıcı
    // geri gelip login olunca login() clearUserScopedState çağırmaz (u.id eşleşir)
    // ve GSPRWizard restore'u localStorage'dan kaldığı yeri geri yükler. Farklı
    // kullanıcı login olursa taslak yine login()'de temizlenir → sızıntı yok.
    if (!opts?.keepDraft) {
      localStorage.removeItem(USER_KEY);
      clearUserScopedState();
    }
  };

  // apiFetch 401'de "auth:logout" event'i fırlatır. Bu İSTEMSİZ bir düşüştür
  // (token süresi doldu), kullanıcının kasıtlı çıkışı değil — bu yüzden taslağı
  // koruyarak oturumu düşürürüz. Kasıtlı çıkış için LeftNav parametresiz logout()
  // çağırır (App.tsx).
  useEffect(() => {
    const onTokenExpired = () => logout({ keepDraft: true });
    window.addEventListener("auth:logout", onTokenExpired);
    return () => window.removeEventListener("auth:logout", onTokenExpired);
  }, []);

  return <AuthCtx.Provider value={{ token, user, login, logout }}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
